import { useState } from 'react';
import { compressImage } from '@core/utils/image-compression';
import { useUploadImages } from '@base/state/use-upload-images';
import type { UploadImagesResult } from '@base/api/image-api';

export interface UseImageUploadParams {
  ticketName?: string;
  defectLineName?: string;
}

export interface UploadImagesOptions {
  ticketName?: string;
  defectLineName?: string;
  maxRetries?: number;
}

export interface ImageUploadState {
  status: 'pending' | 'compressing' | 'uploading' | 'done' | 'error';
  progress: number;
  errorMessage: string | null;
}

export function useImageUpload(params: UseImageUploadParams = {}) {
  const [uploadStates, setUploadStates] = useState<Map<number, ImageUploadState>>(new Map());
  const uploadMutation = useUploadImages();

  const updateImageState = (index: number, state: Partial<ImageUploadState>) => {
    setUploadStates((prev) => {
      const next = new Map(prev);
      const current = next.get(index) ?? {
        status: 'pending' as const,
        progress: 0,
        errorMessage: null,
      };
      next.set(index, { ...current, ...state });
      return next;
    });
  };

  const uploadImages = async (
    files: File[],
    options: UploadImagesOptions = {}
  ): Promise<UploadImagesResult> => {
    const ticketName = options.ticketName ?? params.ticketName;
    const defectLineName = options.defectLineName ?? params.defectLineName;
    const maxRetries = options.maxRetries ?? 2;

    if (!ticketName || !defectLineName) {
      throw new Error('Missing ticket_name or defect_line_name for image upload');
    }

    const compressedFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Step 1: Compress (with fallback to original if compression fails)
      updateImageState(i, { status: 'compressing', progress: 0 });
      let compressedFile: File;
      try {
        compressedFile = await compressImage(file);
        // Guard: reject empty/corrupt files (common in WebView file picker)
        if (compressedFile.size === 0) {
          console.warn(`[ImageUpload] ⚠️ Compressed file[${i}] is 0 bytes, using original`);
          compressedFile = file;
        }
        updateImageState(i, { progress: 50 });
      } catch (error) {
        // In WebView, compression can fail due to WebWorker/Canvas issues.
        // Fall back to the original file instead of aborting the entire upload.
        console.warn(
          `[ImageUpload] ⚠️ Compression failed for file[${i}], using original:`,
          error instanceof Error ? error.message : error
        );
        compressedFile = file;
        updateImageState(i, { progress: 50 });
      }
      compressedFiles.push(compressedFile);
    }

    for (let i = 0; i < compressedFiles.length; i++) {
      updateImageState(i, { status: 'uploading', progress: 50 });
    }

    try {
      let attempt = 0;
      let result: UploadImagesResult | undefined;
      while (!result) {
        try {
          result = await uploadMutation.mutateAsync({
            ticketName,
            defectLineName,
            files: compressedFiles,
          });
        } catch (error) {
          if (attempt >= maxRetries) {
            throw error;
          }
          attempt += 1;
        }
      }

      for (let i = 0; i < compressedFiles.length; i++) {
        updateImageState(i, { status: 'done', progress: 100 });
      }

      return result;
    } catch (error) {
      for (let i = 0; i < compressedFiles.length; i++) {
        updateImageState(i, {
          status: 'error',
          errorMessage: `Tải ảnh lên thất bại: ${error instanceof Error ? error.message : 'Không rõ nguyên nhân'}`,
        });
      }
      throw error;
    }
  };

  const retryUpload = async (
    fileIndex: number,
    files: File[],
    options: UploadImagesOptions = {}
  ): Promise<UploadImagesResult> => {
    updateImageState(fileIndex, { status: 'pending', progress: 0 });
    return uploadImages(files, options);
  };

  return {
    uploadImages,
    retryUpload,
    uploadStates,
    isUploading: uploadMutation.isPending,
    reset: () => setUploadStates(new Map()),
  };
}
